import type * as React from 'react'
import { cn } from '@/lib/utils'

export const inputBase = cn(
  'flex w-full min-w-0 rounded-lg border border-input bg-card px-3 text-sm text-foreground shadow-elevation-1',
  'transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground/70',
  'hover:border-foreground/20 focus-visible:border-primary/60 focus-visible:outline-none',
  'focus-visible:ring-[3px] focus-visible:ring-ring/40 dark:bg-white/[0.03]',
  'disabled:cursor-not-allowed disabled:opacity-60',
  'aria-invalid:border-destructive/70 aria-invalid:ring-destructive/20 aria-invalid:focus-visible:ring-destructive/25',
)

interface InputProps extends React.ComponentProps<'input'> {
  leading?: React.ReactNode
  trailing?: React.ReactNode
}

export function Input({ className, leading, trailing, type = 'text', ...props }: InputProps) {
  const input = (
    <input
      type={type}
      data-slot="input"
      className={cn(
        inputBase,
        'h-9 py-1 file:mr-3 file:border-0 file:bg-transparent file:font-medium file:text-sm',
        leading && 'pl-9',
        trailing && 'pr-9',
        !leading && !trailing && className,
      )}
      {...props}
    />
  )
  if (!leading && !trailing) return input
  return (
    <div className={cn('relative flex items-center', className)}>
      {leading && (
        <span className="pointer-events-none absolute left-3 flex text-muted-foreground [&_svg]:size-4">
          {leading}
        </span>
      )}
      {input}
      {trailing && (
        <span className="absolute right-2 flex items-center text-muted-foreground">{trailing}</span>
      )}
    </div>
  )
}

export function Textarea({ className, ...props }: React.ComponentProps<'textarea'>) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(inputBase, 'min-h-20 resize-y py-2 leading-relaxed', className)}
      {...props}
    />
  )
}
